<template>
  <span
    class="send-message-channel-menu"
    :class="{'send-message-channel-menu--list-item': activatorType === 'list-item'}"
  >
    <v-menu v-bind="resolvedMenuProps">
      <template v-slot:activator="{ on, attrs }">
        <v-list-item
          v-if="activatorType === 'list-item'"
          v-bind="attrs"
          class="justify-center"
          :disabled="activatorDisabled"
          @click.prevent="openChannelMenu($event, on)"
        >
          <v-progress-circular
            v-if="loading"
            indeterminate
            size="16"
            width="2"
            class="mr-2"
          />
          {{ activatorText }}
        </v-list-item>
        <v-btn
          v-else
          v-bind="getButtonProps(attrs)"
          :disabled="activatorDisabled"
          :loading="loading"
          @click.prevent="openChannelMenu($event, on)"
        >
          {{ activatorText }}
        </v-btn>
      </template>
      <v-list dense outlined class="text-body-2">
        <v-list-item
          v-if="showLine"
          class="justify-center"
          @click="$refs.SendLineMessageV2.sendMessageDialog = true"
        >
          LINE訊息{{ sendLineSuffix }}
        </v-list-item>
        <v-list-item
          v-if="showSms"
          class="justify-center"
          @click="$refs.smsMessageV2.sendMessageDialog = true"
        >
          簡訊{{ sendSmsSuffix }}
        </v-list-item>
      </v-list>
    </v-menu>
    <SendLineMessageV2
      v-if="showLine"
      ref="SendLineMessageV2"
      :btnProps="{text: true, small: true}"
      :patient="patient"
      style="display: none;"
    />
    <SendSmsMessageV2
      v-if="showSms"
      v-show="false"
      ref="smsMessageV2"
      :btnProps="{text: true, small: true}"
      :patient="patient"
    />
  </span>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import {
  getChannelQuotaAPI,
  getMessageSettingAPI,
  getPatientChannelBindStatusAPI,
  getSMSQuotaChannelInfoAPI
} from "@/api";
import SendLineMessageV2 from "@/components/utils/SendLineMessageV2.vue";
import SendSmsMessageV2 from "@/components/utils/SendSmsMessageV2.vue";

export default {
  name: "SendMessageChannelMenu",

  components: {
    SendLineMessageV2,
    SendSmsMessageV2
  },

  props: {
    patient: {
      type: Object,
      required: true
    },
    activatorText: {
      type: String,
      default: '傳送訊息'
    },
    activatorType: {
      type: String,
      default: 'button',
      validator: value => ['button', 'list-item'].includes(value)
    },
    buttonProps: {
      type: Object,
      default: null
    },
    menuProps: {
      type: Object,
      default: () => ({})
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showLine: {
      type: Boolean,
      default: true
    },
    showSms: {
      type: Boolean,
      default: true
    },
    useProvidedBindStatus: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: false,
      sendLineSuffix: '',
      sendSmsSuffix: ''
    }
  },

  computed: {
    ...mapGetters([
      'axiosParamData',
      'ownChannelLoaded',
      'hasOwnChannel'
    ]),
    resolvedButtonProps() {
      return this.buttonProps || {
        color: 'primary',
        small: true
      }
    },
    resolvedMenuProps() {
      return {
        offsetX: true,
        left: true,
        ...this.menuProps
      }
    },
    activatorDisabled() {
      return this.disabled || this.loading || !this.patient
    }
  },

  methods: {
    ...mapMutations({
      setOwnChannel: 'setOwnChannel'
    }),
    getButtonProps(attrs) {
      return {
        ...this.resolvedButtonProps,
        ...attrs
      }
    },
    async openChannelMenu(event, on) {
      if (this.activatorDisabled) return

      this.loading = true
      try {
        await this.ensureOwnChannel()

        const [bindData, smsQuota] = await Promise.all([
          this.getLineBindData(),
          this.getSmsQuota()
        ])

        this.sendSmsSuffix = ` - 剩餘${smsQuota}點`
        await this.setLineSuffix(bindData)
        await on.click(event)
      } catch (e) {
        console.error(e)
        this.$notify.error(this.$t('GENERAL.oops'))
      } finally {
        this.loading = false
      }
    },
    async ensureOwnChannel() {
      if (this.ownChannelLoaded) return

      const res = await getMessageSettingAPI(this.axiosParamData)
      this.setOwnChannel(res.data.own_channel)
    },
    async getLineBindData() {
      if (this.useProvidedBindStatus) {
        const isBind = Number(this.patient.bindStatus) === 1
        return {
          own_channel_bind_status: isBind ? 1 : 0,
          dentco_channel_bind_status: isBind ? 1 : 0
        }
      }

      const res = await getPatientChannelBindStatusAPI(this.patient.patientCode, this.axiosParamData)
      return res.data
    },
    async getSmsQuota() {
      const res = await getSMSQuotaChannelInfoAPI(this.axiosParamData).catch(err => err.response)
      return res.data?.quota
    },
    async setLineSuffix(bindData) {
      if (this.useProvidedBindStatus) {
        if (Number(this.patient.bindStatus) === 1) {
          const lineQuota = await this.getLineQuota()
          this.sendLineSuffix = lineQuota > 0 ? ' - 可發送' : ' - 額度不足'
        } else {
          this.sendLineSuffix = ' - 未綁定'
        }
        return
      }

      let lineBindStatus = false

      if (this.hasOwnChannel) {
        lineBindStatus = bindData?.own_channel_bind_status === 1

        if (lineBindStatus) {
          const lineQuota = await this.getLineQuota()
          this.sendLineSuffix = lineQuota > 0 ? ' - 可發送' : ' - 額度不足'
        } else {
          this.sendLineSuffix = ' - 未綁定'
        }
      } else {
        lineBindStatus = bindData?.dentco_channel_bind_status === 1
        this.sendLineSuffix = lineBindStatus ? ' - 可發送' : ' - 未綁定'
      }
    },
    async getLineQuota() {
      const res = await getChannelQuotaAPI(this.axiosParamData)
      return Number(res.data.quota) - Number(res.data.total_usage)
    }
  }
}
</script>

<style scoped>
.send-message-channel-menu {
  display: inline-block;
}

.send-message-channel-menu--list-item {
  display: block;
}
</style>
